import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Link2, Unlink, RefreshCw, BookOpen } from 'lucide-react';
import firstName from '@/utils/firstName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JefeTutoresDashboard = ({ user, onLogout }) => {
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [aRes, tRes, gRes] = await Promise.all([
        axios.get(`${API}/jefe-tutores/asignaciones`, { headers }),
        axios.get(`${API}/jefe-tutores/profesores-disponibles`, { headers }),
        axios.get(`${API}/jefe-tutores/grupos-disponibles`, { headers }),
      ]);
      setAssignments(aRes.data);
      setTeachers(tRes.data);
      setGroups(gRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const assignedTeacherIds = new Set(assignments.map(a => a.teacher_id));
  const assignedGroupIds = new Set(assignments.map(a => a.group_id));
  const availableTeachers = teachers.filter(t => !assignedTeacherIds.has(t.id));
  const availableGroups = groups.filter(g => !assignedGroupIds.has(g.id));

  const handleAssign = async () => {
    if (!selectedTeacher || !selectedGroup) {
      toast.error('Selecciona un profesor y un grupo');
      return;
    }
    try {
      await axios.post(`${API}/jefe-tutores/asignar`, {
        teacher_id: selectedTeacher,
        group_id: selectedGroup
      }, { headers });
      toast.success('Profesor vinculado al grupo');
      setAssignDialog(false);
      setSelectedTeacher('');
      setSelectedGroup('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleUnlink = async (assignmentId, teacherName) => {
    if (!window.confirm(`¿Desvincular a ${teacherName} de su grupo tutorado?`)) return;
    try {
      await axios.delete(`${API}/jefe-tutores/desvincular/${assignmentId}`, { headers });
      toast.success('Vinculacion eliminada');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al desvincular');
    }
  };

  if (loading) return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-teal-500" /></div>
    </Layout>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="mb-6">
          {firstName(user?.name) && (
            <p className="text-sm text-slate-500 mb-0.5" data-testid="welcome-greeting">
              Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span>
            </p>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="jt-dashboard-title">Panel de Jefe de Tutores</h1>
          <p className="text-sm text-slate-600">Asignacion de grupos tutorados a profesores</p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-teal-600">{assignments.length}</div>
            <div className="text-sm text-slate-600">Asignaciones</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-slate-600">{teachers.length}</div>
            <div className="text-sm text-slate-600">Profesores</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-slate-600">{groups.length}</div>
            <div className="text-sm text-slate-600">Grupos</div>
          </Card>
        </div>

        <Card className="p-4" data-testid="jt-assignments-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-500" /> Grupos Tutorados Asignados
            </h2>
            <Button
              onClick={() => setAssignDialog(true)}
              className="bg-teal-500 hover:bg-teal-600 text-white"
              data-testid="jt-assign-btn"
              disabled={availableTeachers.length === 0 || availableGroups.length === 0}
            >
              <Link2 className="w-4 h-4 mr-2" /> Asignar Grupo
            </Button>
          </div>

          {availableTeachers.length === 0 && availableGroups.length === 0 && assignments.length > 0 && (
            <p className="text-xs text-slate-500 mb-3">Todos los profesores y grupos estan asignados</p>
          )}

          {assignments.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No hay asignaciones de grupos tutorados</p>
              <p className="text-xs text-slate-400 mt-1">Vincula un profesor a un grupo para comenzar</p>
            </div>
          ) : (
            <div className="space-y-2">
              {assignments.map(a => (
                <div key={a.id} className="p-4 border rounded-lg flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`jt-assignment-${a.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="bg-teal-50 w-10 h-10 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{a.teacher_name}</p>
                      <p className="text-xs text-slate-500">{a.teacher_email}</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Link2 className="w-4 h-4 text-teal-500" />
                      <span className="font-medium text-teal-700 bg-teal-50 px-3 py-1 rounded-full">{a.group_name}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleUnlink(a.id, a.teacher_name)}
                    data-testid={`jt-unlink-${a.id}`}
                  >
                    <Unlink className="w-4 h-4 mr-1" /> Desvincular
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Asignar Grupo Tutorado</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Profesor</Label>
                <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                  <SelectTrigger data-testid="jt-select-teacher">
                    <SelectValue placeholder="Selecciona un profesor" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTeachers.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grupo</Label>
                <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                  <SelectTrigger data-testid="jt-select-group">
                    <SelectValue placeholder="Selecciona un grupo" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableGroups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAssign} className="w-full bg-teal-500 hover:bg-teal-600" data-testid="jt-confirm-assign">
                <Link2 className="w-4 h-4 mr-2" /> Vincular
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default JefeTutoresDashboard;
